import debugFn from 'debug';
import { usePostHog } from 'posthog-js/react';
import { useCallback } from 'react';

const debug = debugFn('usePostHogCapture');

export interface UsePostHogCaptureOptions {
  /** When set, event names are sent as `domain_eventName` (e.g. stream_date_changed). */
  domain?: string;
}

/**
 * Returns a stable capture function that no-ops when PostHog is not available.
 * Use for custom event tracking (e.g. stream_date_changed, button_clicked).
 * Optionally pass a domain so all events are prefixed (e.g. domain "stream" + "date_changed" → "stream_date_changed").
 */
export function usePostHogCapture(options?: UsePostHogCaptureOptions) {
  const { domain } = options ?? {};
  const posthog = usePostHog();

  const capture = useCallback(
    (eventName: string, properties?: Record<string, unknown>) => {
      const finalEventName = domain ? `${domain}:${eventName}` : eventName;

      if (!posthog) {
        debug('[PostHog] would capture:', finalEventName, properties);
        return;
      }

      posthog.capture(finalEventName, properties);
    },
    [posthog, domain],
  );

  return capture;
}
